import graphql__184f07b03e039a9e438c from "./__generated__/ActivityPanelQuery.graphql.ts";
import graphql__6a9204e11042eb640f10 from "./__generated__/ActivityPanelCustomerAccountQuery.graphql.ts";
import graphql__695fbd806025da943052 from "./__generated__/ActivityPanelRefreshQuery.graphql.ts";
import graphql__7dfff06cd6069f46e0d5 from "./__generated__/ActivityPanelCustomerAccountRefreshQuery.graphql.ts";import * as React from "react";
import {useState, useCallback} from "react";
import {graphql, useLazyLoadQuery, fetchQuery, useRelayEnvironment} from "react-relay";
import {Link} from "react-router-dom";
import Panel from "./presentational/Panel";
import {useIntl} from "react-intl";
import {ActivityPanelQuery} from "./__generated__/ActivityPanelQuery.graphql";
import {ActivityPanelCustomerAccountQuery} from "./__generated__/ActivityPanelCustomerAccountQuery.graphql";
import ActivityTimeline from "./ActivityTimeline";
import {useActivityStream} from "../hooks/useActivityStream";

type Props = {
    contactId?: string;
    contactGraphqlId?: string;
    customerAccountId?: string;
    customerAccountGraphqlId?: string;
}

type Activity = {
    readonly id: string;
    readonly source: string;
    readonly type: string;
    readonly occurredAt: string;
    readonly metadata: any;
    readonly actorName: string | null;
    readonly body: string | null;
};

const ActivityPanel: React.FC<Props> = ({contactId, contactGraphqlId, customerAccountId, customerAccountGraphqlId}) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();

    const contactData = useLazyLoadQuery<ActivityPanelQuery>(
        graphql__184f07b03e039a9e438c,
        {contactIdentifier: contactId || '', first: 5},
        {fetchPolicy: contactId ? 'store-or-network' : 'store-only'}
    );

    const customerAccountData = useLazyLoadQuery<ActivityPanelCustomerAccountQuery>(
        graphql__6a9204e11042eb640f10,
        {customerAccountIdentifier: customerAccountId || '', first: 5},
        {fetchPolicy: customerAccountId ? 'store-or-network' : 'store-only'}
    );

    const initialActivityResult = contactId
        ? contactData.contactActivities
        : customerAccountData.customerAccountActivities;

    const [activities, setActivities] = useState<ReadonlyArray<Activity>>(
        initialActivityResult?.activities || []
    );
    const [hasMore, setHasMore] = useState(initialActivityResult?.hasMore || false);

    // Subscribe to realtime activity updates via SSE
    const handleNewActivity = useCallback(() => {
        if (contactId) {
            fetchQuery(
                environment,
                graphql__695fbd806025da943052,
                {contactIdentifier: contactId, first: 5}
            ).subscribe({
                next: (data: any) => {
                    setActivities(data.contactActivities?.activities || []);
                    setHasMore(data.contactActivities?.hasMore || false);
                }
            });
        } else if (customerAccountId) {
            fetchQuery(
                environment,
                graphql__7dfff06cd6069f46e0d5,
                {customerAccountIdentifier: customerAccountId, first: 5}
            ).subscribe({
                next: (data: any) => {
                    setActivities(data.customerAccountActivities?.activities || []);
                    setHasMore(data.customerAccountActivities?.hasMore || false);
                }
            });
        }
    }, [environment, contactId, customerAccountId]);

    useActivityStream({
        subjectType: contactId ? 'contact' : 'customer_account',
        subjectIdentifier: contactId || customerAccountId,
        onActivity: handleNewActivity,
        enabled: !!(contactId || customerAccountId),
    });

    const activitiesLink = contactId
        ? `/contacts/${contactGraphqlId}/activities`
        : `/customers/${customerAccountGraphqlId}/activities`;

    return (
        <Panel headline={intl.formatMessage({id: 'activity.recentActivity', defaultMessage: 'Recent Activity'})}>
            <ActivityTimeline activities={activities} />
            {hasMore && (
                <div className="text-center pt-4 border-t border-gray-100 mt-4">
                    <Link
                        to={activitiesLink}
                        className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                    >
                        {intl.formatMessage({id: 'activity.viewAll', defaultMessage: 'View all activities'})} →
                    </Link>
                </div>
            )}
        </Panel>
    );
};

export default ActivityPanel;
